import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import '../SharedStyles.css';
import ReviewItem from '../../components/ReviewItem/ReviewItem';
import ReviewDialog from '../../components/ReviewDialog/ReviewDialog';
import EmptyStateEnhanced from '../../components/EmptyStateEnhanced/EmptyStateEnhanced';
import { useReviews } from '../../hooks/useReviews';
import { useSubscription } from '../../hooks/useSubscription';
import SubscriptionDialog from '../../components/SubscriptionDialog/SubscriptionDialog';
import './ReviewsView.css';

const ReviewsView = () => {
  const { user } = useAuth();
  const { selectedTeam, selectedProject, projects } = useWorkspace();
  const navigate = useNavigate();
  const location = useLocation();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- used for animation class
  const [isNavigatingToReview, setIsNavigatingToReview] = useState(false);
  const [, setSelectedReviewId] = useState<string | null>(null);
  const [isReviewDialogOpen, setIsReviewDialogOpen] = useState(false);
  const [isCreatingReview, setIsCreatingReview] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSubscriptionDialogOpen, setIsSubscriptionDialogOpen] = useState(false);
  const { hasActiveSubscription, isLoading: isSubscriptionLoading } = useSubscription();

  // Use the custom hook to manage reviews with the selected project
  const {
    reviews,
    error,
    createReview,
    toggleReviewCompletion
  } = useReviews(selectedProject?.id);
  
  // Reset navigation state when returning to reviews page
  useEffect(() => {
    if (location.pathname === '/reviews') {
      setIsNavigatingToReview(false);
      setSelectedReviewId(null);
    }
  }, [location.pathname]);

  // Listen for search query updates from the toolbar
  useEffect(() => {
    const handleSearchUpdate = (event: CustomEvent<{ searchQuery: string }>) => {
      setSearchQuery(event.detail.searchQuery);
    };

    window.addEventListener('update-search-query', handleSearchUpdate as EventListener);
    return () => {
      window.removeEventListener('update-search-query', handleSearchUpdate as EventListener);
    };
  }, []);

  // Listen for review dialog open event
  useEffect(() => {
    const handleOpenReviewDialog = () => {
      setIsReviewDialogOpen(true);
    };

    window.addEventListener('open-review-dialog', handleOpenReviewDialog);
    return () => {
      window.removeEventListener('open-review-dialog', handleOpenReviewDialog);
    };
  }, []);

  const handleOpenReviewDialog = () => {
    if (!isSubscriptionLoading && !hasActiveSubscription) {
      setIsSubscriptionDialogOpen(true);
      return;
    }
    setIsReviewDialogOpen(true);
  };

  const handleCloseReviewDialog = () => {
    setIsReviewDialogOpen(false);
  };

  const handleCreateReview = async (title: string, description: string) => {
    if (!user || !selectedProject) {
      return;
    }

    if (!isSubscriptionLoading && !hasActiveSubscription) {
      setIsSubscriptionDialogOpen(true);
      return;
    }
    
    try {
      setIsCreatingReview(true);
      // Create the review in the background
      await createReview(title, description);
      // Close the dialog after successful creation
      setIsReviewDialogOpen(false);
    } catch (error) {
      // console.error('Error creating review:', error);
    } finally {
      setIsCreatingReview(false);
    }
  };

  const handleToggleComplete = (reviewId: string) => {
    toggleReviewCompletion(reviewId);
  };

  const handleReviewClick = (reviewId: string) => {
    setIsNavigatingToReview(true);
    setSelectedReviewId(reviewId);
    
    // Delay navigation to allow animations to complete - reduced for snappier feel
    setTimeout(() => {
      navigate(`/app/review/${reviewId}`);
    }, 200);
  };
  
  // Filter reviews by completion status and search query
  const filteredReviews = reviews.filter(review => 
    review.title.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const openReviews = filteredReviews.filter(review => !review.completed);
  const completedReviews = filteredReviews.filter(review => review.completed);
  
  // Get team projects for display purposes
  const teamProjects = selectedTeam && projects 
    ? projects.filter(project => project.team_id === selectedTeam.id) 
    : [];
  
  return (
    <div className="reviews-view">
     
      {!selectedTeam && (
        <EmptyStateEnhanced
          title="No team selected"
          subtitle="Please select a team to view projects and reviews."
          fullView={true}
        />
      )}
      
      {selectedTeam && teamProjects.length === 0 && (
        <EmptyStateEnhanced
          title="No projects in this team"
          subtitle="Create your first project to start managing translation content."
          buttonText="Create Project"
          onButtonClick={() => window.dispatchEvent(new CustomEvent('open-create-project-dialog', { detail: { teamId: selectedTeam.id } }))}
          fullView={true}
        />
      )}
      
      {selectedTeam && teamProjects.length > 0 && !selectedProject && (
        <EmptyStateEnhanced
          title="There are no projects in this team"
          subtitle="Please select a project to view reviews."
          fullView={true}
        />
      )}
      
      {error && (
        <EmptyStateEnhanced
          title="Error"
          subtitle={`${error}`}
          fullView={true}
        />
      )}
      
      {selectedProject && openReviews.length === 0 && completedReviews.length === 0 && (
        <EmptyStateEnhanced
          title="No translation reviews"
          subtitle={searchQuery ? `No reviews matching "${searchQuery}" found.` : 'Create your first translation review to manage content updates for translators.'}
          buttonText="Create Review"
          onButtonClick={handleOpenReviewDialog}
          fullView={true}
        />
      )}
      
      {selectedProject && (openReviews.length > 0 || completedReviews.length > 0) && (
        <div className="view-container">
          <div className="content">
            {openReviews.length > 0 && (
              <div className="section">
                <div className="section-header">
                  <div className="header-title">To do</div>
                  <div className="header-meta">
                    <div className="header-keys"></div>
                    <div className="header-owner"></div>
                  </div>
                </div>

                <div className="list-container">
                  {openReviews.map(review => (
                    <ReviewItem 
                      key={review.id}
                      review={review}
                      onToggleComplete={handleToggleComplete}
                      onClick={() => handleReviewClick(review.id)}
                      className="list-item"
                    />
                  ))}
                </div>
              </div>
            )}
            {completedReviews.length > 0 && (
              <div className="section">
                <div className="section-header">
                  <h2>Completed</h2>
                </div>
                <div className="list-container-secondary">
                  {completedReviews.map(review => (
                    <ReviewItem 
                      key={review.id}
                      review={review}
                      onToggleComplete={handleToggleComplete}
                      onClick={() => handleReviewClick(review.id)}
                      className="list-item"
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      <ReviewDialog 
        isOpen={isReviewDialogOpen}
        onClose={handleCloseReviewDialog}
        onSave={handleCreateReview}
        isLoading={isCreatingReview}
      />
      <SubscriptionDialog
        isOpen={isSubscriptionDialogOpen}
        onClose={() => setIsSubscriptionDialogOpen(false)}
      />
    </div>
  );
};

export default ReviewsView; 